import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import React, { useEffect, useMemo, useRef } from 'react'
import type { Location } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { WATERMARKING } from '@disco/constants/FeatureTypes'
import { PLAYLIST } from '@disco/constants/ItemTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updatePlaylistBaseObj } from '@disco/state-management/actions/playlist-actions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import {
  loadNextWatermarking,
  loadWatermarking,
  updateWatermarkingPlaylistsSearchParams,
} from '@disco/state-management/actions/watermarking-actions'
import type { State } from '@disco/state-management/reducers/watermarking'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { useWatermarkingQuota } from '@disco/utils/getWatermarkingQuota'

import type { Playlist } from 'types/playlist'
import type { PopoverItemType } from 'types/ui'

import PlaylistInfo from 'components/common/playlist-info'
import WatermarkingStatusFlag from 'components/explorer/watermarking-status-flag'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import InfiniteScroll from 'components/ui/infinite-scroll'
import InlineLink from 'components/ui/inline-link'
import Input from 'components/ui/input'
import Spinner from 'components/ui/spinner'

import WatermarkingEmptyState from 'pages/watermarking/watermarking-empty-state'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  children: React.ReactNode
  location: Location
}

const WatermarkingPlaylists = ({
  children,
  location,
}: Props): null | React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isFeatureEnabled = useFeatureEnabled(WATERMARKING)
  const watermarkingQuota = useWatermarkingQuota()
  const {
    filters,
    items,
    nextPage,
    searching,
    searchTerm,
  }: State['playlists'] = useSelector(state => state.watermarking.playlists)
  useEffect(() => {
    if (watermarkingQuota.quotaUsed > 0) {
      dispatch(loadWatermarking(searchTerm, filters, PLAYLIST))
    }
  }, [])
  const debouncedQuery = useRef(
    debounce((searchTerm, filters) => {
      dispatch(loadWatermarking(searchTerm, filters, PLAYLIST))
    }, 300),
  ).current

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value
    dispatch(
      updateWatermarkingPlaylistsSearchParams({
        searchTerm,
      }),
    )
    debouncedQuery(searchTerm, filters)
  }

  const loadNext = (next: string) => {
    dispatch(loadNextWatermarking(next, PLAYLIST))
  }

  const getEllipsisMenuOptions = playlist => {
    const { disable_watermark_warning, playlist_id, watermark } = playlist
    return [
      {
        label: !watermark
          ? 'Watermark playlist'
          : 'Remove watermarking from playlist',
        onClick: () => {
          if (watermark) {
            navigate(`/watermarking/${playlist_id}/remove-watermarking`)
          } else {
            const update = {
              watermark: !watermark,
            }
            const message = {
              error: 'Error!',
              success: !watermark
                ? 'Playlist watermarked!'
                : 'Watermarking removed from playlist!',
            }
            dispatch(updatePlaylistBaseObj(playlist_id, update, message))
          }
        },
      },
      {
        label: !disable_watermark_warning
          ? 'Remove watermaking warning text'
          : 'Add watermarking warning text',
        onClick: () => {
          const update = {
            disable_watermark_warning: !disable_watermark_warning,
          }
          const message = {
            error: 'Error!',
            success: !disable_watermark_warning
              ? 'Warning text removed from playlist!'
              : 'Warning text added from playlist!',
          }
          dispatch(updatePlaylistBaseObj(playlist_id, update, message))
        },
      },
      {
        label: 'View stats',
        onClick: () => {
          navigate(`/watermarking/${playlist_id}/stats`, {
            state: {
              returnTo: location.pathname,
            },
          })
        },
      },
      {
        label: 'Edit playlist',
        onClick: () => {
          dispatch(createNewShortlist(playlist))
          dispatch(sendMixpanelEvent('Edit playlist via playlist menu'))
          navigate('/')
        },
      },
      {
        label: 'Leak trace',
        link: {
          url: `/watermarking/leak-tracing`,
        },
        type: 'router-link',
      },
    ] as PopoverItemType[]
  }

  const FILTER_OPTIONS: PopoverItemType[] = useMemo(
    () => [
      {
        label: 'Show all',
        selected: filters.watermarking_status === 'non-empty',
        value: 'non-empty',
      },
      {
        label: 'In progress',
        selected: filters.watermarking_status === 'processing',
        value: 'processing',
      },
      {
        label: 'Failed',
        selected: filters.watermarking_status === 'failed',
        value: 'failed',
      },
      {
        label: 'Watermark removed',
        selected: filters.watermarking_status === 'watermark_removed',
        value: 'watermark_removed',
      },
      {
        label: 'Watermarked',
        selected: filters.watermarking_status === 'watermarked',
        value: 'watermarked',
      },
    ],
    [filters],
  )
  let watermarkPlaylistTooltip = null

  if (!isFeatureEnabled) {
    watermarkPlaylistTooltip = (
      <>
        To watermark playlists, re-subscribe to watermarking{' '}
        <InlineLink to="/settings/billing">here</InlineLink>
      </>
    )
  }

  if (watermarkingQuota.overQuota) {
    watermarkPlaylistTooltip = (
      <>
        You’ve reached your watermarking quota. To watermark more playlists,{' '}
        <InlineLink to="/settings/billing">upgrade</InlineLink> for more
        watermarking quota, or remove watermarking from another playlist.
      </>
    )
  }

  if (watermarkingQuota.quotaUsed === 0) return null
  return (
    <>
      <div className="Watermarking_Header">
        <div className="Watermarking_SearchWrapper">
          <Input
            icon="search"
            placeholder="Search"
            onChange={updateSearchQuery}
            value={searchTerm}
          />
          <EllipsisMenu
            caption={
              FILTER_OPTIONS.find(({ selected }) => selected)?.label ||
              'Filter by'
            }
            buttonSize="large"
            compact
            options={FILTER_OPTIONS}
            onOptionClick={value => {
              const filters = {
                watermarking_status: value,
              }
              dispatch(
                updateWatermarkingPlaylistsSearchParams({
                  filters,
                }),
              )
              debouncedQuery(searchTerm, filters)
            }}
          />
        </div>
        <div>
          <Button
            appearance="primary"
            caption="Watermark Playlist"
            disabled={!isFeatureEnabled || watermarkingQuota.overQuota}
            link="/watermarking/playlists/watermark-playlist"
            tooltip={watermarkPlaylistTooltip}
          />
        </div>
      </div>

      {searching && items.length === 0 && <Spinner />}

      {items.length === 0 ? (
        !searching && <WatermarkingEmptyState type={PLAYLIST} />
      ) : (
        <InfiniteScroll next={nextPage} loadNext={loadNext}>
          <DataTable
            appearance="bordered"
            columns={[
              {
                name: 'Playlists',
                render: (playlist: Playlist) => (
                  <PlaylistInfo hideWatermarkingFlag playlist={playlist} />
                ),
              },
              {
                name: 'Status',
                render: (playlist: Playlist) => (
                  <WatermarkingStatusFlag
                    key={playlist.name}
                    type={playlist.type}
                    watermarking_status={playlist.watermarking_status}
                  />
                ),
              },
              {
                contentType: 'button',
                name: 'Actions',
                width: 'content',
                render: (playlist: Playlist) => (
                  <EllipsisMenu
                    compact
                    disabled={!isFeatureEnabled}
                    hasArrow={false}
                    icon="ellipsis"
                    options={getEllipsisMenuOptions(playlist)}
                  />
                ),
              },
            ]}
            data={uniqBy(items, 'id') as any}
          />
        </InfiniteScroll>
      )}
      {children}
    </>
  )
}

export default WatermarkingPlaylists
